import { Box, Button, Flex, Input, VStack } from "@chakra-ui/react";
import { flexRender, type Table } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useState } from "react";

export type Cell = {
	id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	row: any;
};

type ClientSideTableMeta = {
	headerClassName?: string;
	rowClassName?: string;
};

type ClientSideTableProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	table: Table<any>;
	w?: string[];
	flexGrow?: number[];
	toolboxButtons?: boolean;
	actionsComponent?: () => React.ReactNode;
	noDataComponent?: () => React.ReactNode;
	handleCellClick?: (row: Cell) => void;
	noSearch?: boolean;
	noPagination?: boolean;
	search?: string;
	setSearch?: (search: string) => void;
	onRowClick?: (row: unknown) => void;
	onRowHighlight?: (row: unknown) => string;
};

function ClientSideTable({
	table,
	w,
	flexGrow,
	toolboxButtons,
	actionsComponent,
	noDataComponent,
	handleCellClick,
	noSearch = false,
	noPagination = false,
	search,
	setSearch,
	onRowClick,
	onRowHighlight,
}: ClientSideTableProps) {
	const [globalFilter, setGlobalFilter] = useState("");

	const localFilter = search ?? globalFilter;
	const setLocalFilter = setSearch ?? setGlobalFilter;

	const handlSetGlobalFilter = useCallback(
		(filter: string) => {
			setLocalFilter(filter);
			table.setGlobalFilter(filter);
		},
		[table, setLocalFilter],
	);

	useEffect(() => {
		if (search) {
			handlSetGlobalFilter(search);
		}
	}, [handlSetGlobalFilter, search]);

	return (
		<>
			{/* Toolbar */}
			{(!noSearch || toolboxButtons) && (
				<Flex my="1rem" justifyContent="space-between">
					<Flex>
						{!noSearch && (
							<Input
								value={localFilter ?? ""}
								onChange={(evt) =>
									handlSetGlobalFilter(String(evt.target.value))
								}
								placeholder="Rechercher"
								w="20rem"
							/>
						)}
					</Flex>
					{toolboxButtons !== false && (
						<>{actionsComponent && actionsComponent()}</>
					)}
				</Flex>
			)}

			<VStack alignItems={"start"} bg="zinc.50" rounded="xl" w="100%" gap="0">
				<Flex w="100%">
					{table.getHeaderGroups().map((headerGroup) => {
						return headerGroup.headers.map((header, index) => {
							const meta = header.column.columnDef.meta as ClientSideTableMeta;
							return (
								<Box
									w={w ? w[index] : "auto"}
									flexGrow={flexGrow ? flexGrow[index] : "auto"}
									key={header.id}
								>
									<Flex
										w={"100%"}
										onClick={header.column.getToggleSortingHandler()}
										justifyContent="space-between"
										px="24px"
										py="12px"
										fontSize={"12px"}
										fontWeight={500}
										lineHeight={"18px"}
										color="gray.500"
										className={meta?.headerClassName}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
										{{
											asc: <ChevronUpIcon h="18px"></ChevronUpIcon>,
											desc: <ChevronDownIcon h="18px"></ChevronDownIcon>,
										}[header.column.getIsSorted() as string] ?? null}
									</Flex>
								</Box>
							);
						});
					})}
				</Flex>

				<Flex
					w={"calc(100% - 8px)"}
					direction={"column"}
					gap="2px"
					mx="4px"
					mb="4px"
				>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row) => {
							return (
								<Flex
									key={row.id}
									height={"72px"}
									px="12px"
									py="16px"
									alignItems={"center"}
									bg="white"
									_first={{ roundedTop: "xl" }}
									_last={{ roundedBottom: "xl" }}
									onClick={() => onRowClick?.(row.original)}
									className={onRowHighlight?.(row.original) || ""}
								>
									{row.getVisibleCells().map((cell, index) => {
										const meta = cell.column.columnDef
											.meta as ClientSideTableMeta;
										return (
											<Box
												w={w ? w[index] : "auto"}
												flexGrow={flexGrow ? flexGrow[index] : "auto"}
												key={cell.id}
												onClick={() => {
													handleCellClick &&
														handleCellClick({
															id: cell.id,
															row: row.original,
														});
												}}
												className={meta?.rowClassName}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</Box>
										);
									})}
								</Flex>
							);
						})
					) : noDataComponent ? (
						noDataComponent()
					) : (
						<Flex w="100%" justifyContent="center">
							<Box
								w="100%"
								rounded="xl"
								bg="white"
								textAlign="center"
								h="44px"
								lineHeight="44px"
								color="gray.500"
							>
								Pas de données pour le moment
							</Box>
						</Flex>
					)}
				</Flex>
			</VStack>

			{!noPagination && (
				<Flex w="100%" justifyContent="center" mt="1rem">
					<Box>
						<Button
							className="border rounded p-1"
							onClick={() => table.firstPage()}
							isDisabled={!table.getCanPreviousPage()}
							variant="outline"
							mr="4px"
						>
							{"<<"}
						</Button>
						<Button
							className="border rounded p-1"
							onClick={() => table.previousPage()}
							isDisabled={!table.getCanPreviousPage()}
							variant="outline"
						>
							{"<"}
						</Button>
						<Box display="inline" mx="10px">
							Page&nbsp;
							{table.getState().pagination.pageIndex + 1}
							&nbsp;/&nbsp;
							{table.getPageCount().toLocaleString()}
						</Box>
						<Button
							className="border rounded p-1"
							onClick={() => table.nextPage()}
							isDisabled={!table.getCanNextPage()}
							variant="outline"
							mr="4px"
						>
							{">"}
						</Button>
						<Button
							className="border rounded p-1"
							onClick={() => table.lastPage()}
							isDisabled={!table.getCanNextPage()}
							variant="outline"
						>
							{">>"}
						</Button>
					</Box>
				</Flex>
			)}
		</>
	);
}
export default ClientSideTable;
